import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  EmptyState,
  InfoHintButton,
  LoadingState,
  PlayerSearchInput,
  SectionHeader,
  StatusBadge,
  Typography,
} from '../../common';
import { formatLeagueDate, leaguePlayerNameClass } from '../../leagues/LeagueMatchViews';
import {
  fetchLeagueTeamAnalysisSource,
  fetchMyLeaguePlayerId,
} from '../../../services/leagueService';
import {
  buildLeaguePlayerAnalysis,
  formatVsTeam,
  formatWinRate,
  resolveLeagueAnalysisPlayerId,
} from '../../../utils/leaguePlayerAnalysis';

const vsTeamHint =
  'Rubber win rate minus the team’s rubber win rate in the same matches. A pair can look poor at 33% and still be the best unit if the team lost 8–1.';

const StatTile = ({ label, value, valueClassName = '', hint }) => (
  <div className="flex min-h-[4.25rem] min-w-0 flex-col justify-between rounded-lg border border-border-medium bg-surface px-2.5 py-2.5 shadow-sm sm:min-h-[4.75rem] sm:px-3 sm:py-3">
    <div className="flex items-start justify-between gap-1">
      <p className="text-[10px] font-semibold uppercase leading-tight tracking-wider text-text-muted sm:text-[11px]">
        {label}
      </p>
      {hint ? <InfoHintButton text={hint} ariaLabel={`About ${label}`} /> : null}
    </div>
    <p className={`mt-1.5 text-xl font-bold tabular-nums leading-none sm:text-2xl ${valueClassName}`}>
      {value}
    </p>
  </div>
);

const vsTeamTone = (delta) => {
  if (delta == null || Math.round(delta) === 0) return 'text-text-primary';
  return delta > 0 ? 'text-success-dark' : 'text-danger-dark';
};

const toSearchPlayer = (player, isYou) => {
  if (!player) return null;
  const name = player.display_name ?? 'League player';
  const label = isYou ? `${name} (You)` : name;
  return {
    id: player.id,
    first_name: label,
    last_name: '',
    name: label,
    gender: player.sex_male === false ? 'female' : 'male',
  };
};

const PartnerNames = ({ ids, playersById }) => {
  const names = ids.map((id) => playersById.get(id)).filter(Boolean);
  if (!names.length) return '—';
  return names.map((player, index) => (
    <React.Fragment key={player.id}>
      {index > 0 ? <span className="text-text-muted"> / </span> : null}
      <span className={leaguePlayerNameClass(player)}>{player.display_name}</span>
    </React.Fragment>
  ));
};

export function ClubLeaguePlayerAnalysis({
  clubId,
  currentUserId,
  teamId,
  seasonId,
  teamsById,
  onOpenFixture,
  canSearchPlayers = false,
  title = 'My performance',
}) {
  const [source, setSource] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [myLeaguePlayerId, setMyLeaguePlayerId] = useState(null);
  const [identityReady, setIdentityReady] = useState(false);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [expandedPartnerId, setExpandedPartnerId] = useState(null);

  useEffect(() => {
    if (!clubId || !currentUserId) {
      setMyLeaguePlayerId(null);
      setIdentityReady(true);
      return undefined;
    }
    let cancelled = false;
    setIdentityReady(false);
    fetchMyLeaguePlayerId(clubId, currentUserId)
      .then((id) => { if (!cancelled) setMyLeaguePlayerId(id); })
      .catch(() => { if (!cancelled) setMyLeaguePlayerId(null); })
      .finally(() => { if (!cancelled) setIdentityReady(true); });
    return () => { cancelled = true; };
  }, [clubId, currentUserId]);

  useEffect(() => {
    if (!teamId || !seasonId) {
      setSource(null);
      setSelectedPlayerId(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchLeagueTeamAnalysisSource(teamId, seasonId)
      .then((next) => {
        if (cancelled) return;
        setSource(next);
        setExpandedPartnerId(null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load league player analysis', err);
        setError(err.message);
        setSource(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [teamId, seasonId]);

  const roster = source?.roster ?? [];

  useEffect(() => {
    const rosterPlayerIds = roster.map((row) => row.player.id);
    setSelectedPlayerId((prev) => resolveLeagueAnalysisPlayerId({
      canSearchPlayers,
      rosterPlayerIds,
      myLeaguePlayerId,
      selectedPlayerId: prev,
    }));
  }, [roster, myLeaguePlayerId, canSearchPlayers]);

  const analysis = useMemo(() => {
    if (!source || !selectedPlayerId || !teamId) return null;
    return buildLeaguePlayerAnalysis({
      leaguePlayerId: selectedPlayerId,
      teamId,
      fixtures: source.fixtures,
      rubbers: source.rubbers,
      lineups: source.lineups,
      playersById: source.playersById,
    });
  }, [source, selectedPlayerId, teamId]);

  if (!teamId || !seasonId) return null;

  if ((loading && !source) || !identityReady) {
    return <LoadingState message={canSearchPlayers ? 'Loading player performance…' : 'Loading your performance…'} />;
  }

  if (error && !source) {
    return <Typography variant="warning">{error}</Typography>;
  }

  if (!roster.length) {
    return (
      <section>
        <SectionHeader title={title} size="md" />
        <EmptyState
          title="No appearances yet"
          description={
            canSearchPlayers
              ? 'Once this team has match cards, rubber records and partners show up here.'
              : 'Once this team has match cards, your rubber record and partners show up here.'
          }
        />
      </section>
    );
  }

  const playersById = source?.playersById ?? new Map();
  const selectedPlayer = roster.find((row) => row.player.id === selectedPlayerId)?.player ?? null;
  const selectedIsYou = selectedPlayerId != null && selectedPlayerId === myLeaguePlayerId;
  const rubbers = analysis?.rubbers;

  return (
    <section className="space-y-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <SectionHeader title={title} size="md" />
          {!canSearchPlayers && selectedPlayer ? (
            <Typography className={`mt-1 truncate text-sm font-semibold ${leaguePlayerNameClass(selectedPlayer)}`}>
              {selectedPlayer.display_name}
            </Typography>
          ) : null}
        </div>
        {canSearchPlayers ? (
          <div className="w-full sm:max-w-md">
            <PlayerSearchInput
              placeholder="Search league players…"
              availablePlayers={roster.map((row) => (
                toSearchPlayer(row.player, row.player.id === myLeaguePlayerId)
              ))}
              selectedPlayer={toSearchPlayer(selectedPlayer, selectedIsYou)}
              onSelectPlayer={(player) => {
                setSelectedPlayerId(player.id);
                setExpandedPartnerId(null);
              }}
              onRemovePlayer={() => {
                setSelectedPlayerId(null);
                setExpandedPartnerId(null);
              }}
            />
          </div>
        ) : null}
      </div>

      {canSearchPlayers && !analysis ? (
        <Typography variant="caption" className="text-text-muted">
          Search a player to see rubber records and partners.
        </Typography>
      ) : null}

      {analysis ? (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-2.5">
            <StatTile
              label="Rubbers"
              value={rubbers.played ? `${rubbers.won}–${rubbers.lost}` : '—'}
            />
            <StatTile
              label="Rubber %"
              value={formatWinRate(rubbers.winRate)}
              valueClassName={vsTeamTone((rubbers.winRate ?? 50) - 50)}
            />
            <StatTile
              label="Vs team"
              value={formatVsTeam(analysis.vsTeam)}
              valueClassName={vsTeamTone(analysis.vsTeam)}
              hint={vsTeamHint}
            />
            <StatTile label="Appearances" value={analysis.appearances || '—'} />
          </div>
          {rubbers.played > 0 ? null : (
            <Typography variant="caption" className="text-text-muted">
              No completed rubbers for this player on this team.
            </Typography>
          )}

          <div>
            <Typography variant="label" className="mb-2 block">
              {selectedIsYou ? 'Your partners' : 'Partners'}
            </Typography>
            {analysis.partners.length === 0 ? (
              <EmptyState
                title="No partners yet"
                description="Partnerships appear once this player shares a rubber on a match card."
              />
            ) : (
              <ul className="space-y-2">
                {analysis.partners.map((partner) => {
                  const open = expandedPartnerId === partner.partnerId;
                  return (
                    <li key={partner.partnerId}>
                      <Card size="small" className="p-0 sm:p-0 overflow-hidden">
                        <button
                          type="button"
                          className="flex min-h-[44px] w-full items-center gap-3 px-3 py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                          aria-expanded={open}
                          onClick={() => setExpandedPartnerId(open ? null : partner.partnerId)}
                        >
                          <div className="min-w-0 flex-1">
                            <Typography className={`truncate text-sm font-semibold ${leaguePlayerNameClass(partner.player)}`}>
                              {partner.player?.display_name ?? 'Partner'}
                            </Typography>
                            <Typography variant="caption" className="text-text-muted">
                              {partner.won}–{partner.lost} rubbers
                              {partner.teamWon + partner.teamLost > 0
                                ? ` · team ${partner.teamWon}–${partner.teamLost}`
                                : ''}
                              {partner.vsTeam != null ? ` · ${formatVsTeam(partner.vsTeam)} vs team` : ''}
                            </Typography>
                          </div>
                          <span className={`text-sm font-bold tabular-nums ${vsTeamTone(partner.vsTeam)}`}>
                            {formatWinRate(partner.winRate)}
                          </span>
                        </button>
                        {open ? (
                          <ul className="border-t border-border-medium">
                            {partner.history.map((row) => {
                              const isHome = row.fixture.home_team_id === teamId;
                              const opponentId = isHome ? row.fixture.away_team_id : row.fixture.home_team_id;
                              const opponentName = teamsById?.get(opponentId)?.name ?? 'Opponent';
                              const hasGames = row.gamesUs + row.gamesThem > 0;
                              return (
                                <li key={row.rubber.id}>
                                  <button
                                    type="button"
                                    className="flex min-h-[44px] w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-surface-subtle focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                                    onClick={() => onOpenFixture?.(row.fixture)}
                                  >
                                    <div className="min-w-0 flex-1">
                                      <Typography variant="caption" className="text-text-muted">
                                        {formatLeagueDate(row.fixture.match_date)} · {isHome ? 'vs' : 'at'} {opponentName}
                                      </Typography>
                                      <Typography variant="caption" className="block text-text-secondary">
                                        vs <PartnerNames ids={row.opponentIds} playersById={playersById} />
                                      </Typography>
                                    </div>
                                    {hasGames ? (
                                      <span className="text-sm font-semibold tabular-nums text-text-primary">
                                        {row.gamesUs}–{row.gamesThem}
                                      </span>
                                    ) : null}
                                    <StatusBadge variant={row.won ? 'success' : 'danger'} size="small">
                                      {row.won ? 'Won' : 'Lost'}
                                    </StatusBadge>
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        ) : null}
                      </Card>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      ) : null}
    </section>
  );
}
